import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { X, Loader2, CheckCircle2, Copy, Check, Award, Crown } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import MFld from "./MFld";
import PaymentPill from "./PaymentPill";
import ModalHeroRow from "./ModalHeroRow";
import RentalSummaryBox from "./RentalSummaryBox";
import { COUNTRIES, DIAL_CODES, DEFAULT_DIAL_CODE } from "./geo";
import DigitalRentalPass from "../DigitalRentalPass";
import {
  fmtEUR,
  isActiveStatus,
  isRequiredStatus,
  OPTIONAL_FIELDS,
  requiredLabel,
} from "./formatters";

/**
 * BookingModal — public 2-step checkout for instantly-bookable vehicles.
 * Step 1 → collect driver & reservation details.
 * Step 2 → order review + deposit summary before hitting `/public/quick-book`.
 */
export default function BookingModal({ vehicle, form, days, settings, extras, onClose }) {
  const [customer, setCustomer] = useState({ full_name: "", email: "", phone: "" });
  // v3.9.7 — Phone split into dial code + local digits. Concatenated
  // as `"+30 12345"` on submit so the server receives a single string.
  const [dialCode, setDialCode] = useState(DEFAULT_DIAL_CODE);
  const [phoneLocal, setPhoneLocal] = useState("");
  const [loyalty, setLoyalty] = useState(null);   // v3.9.6 — tier peek
  const [extra, setExtra] = useState({});
  const [selectedExtras, setSelectedExtras] = useState({});
  const [flight, setFlight]   = useState({ airline_company: "", flight_number: "", flight_time: "" });
  const [ferry, setFerry]     = useState({ company: "", vessel_name: "", arrival_time: "" });
  const [saving, setSaving]   = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const [ibanCopied, setIbanCopied] = useState(false);
  const [step, setStep] = useState(1);

  const cf = settings?.checkout_fields || {};
  const paymentMethods = settings?.payment_methods || ["pickup", "card", "bank_transfer"];
  const bank = settings?.bank_details || {};
  const [paymentMethod, setPaymentMethod] = useState(settings?.default_payment_method || paymentMethods[0] || "pickup");
  useEffect(() => {
    if (settings?.default_payment_method) setPaymentMethod(settings.default_payment_method);
  }, [settings?.default_payment_method]);

  // v3.9.6 — Debounced loyalty peek. Once the customer types a plausible
  // email we ping /public/loyalty/peek and surface their Silver/Gold/VIP
  // badge next to the input, so returning guests feel recognised.
  useEffect(() => {
    const email = (customer.email || "").trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setLoyalty(null); return; }
    const t = setTimeout(() => {
      api.get(`/public/loyalty/peek?email=${encodeURIComponent(email)}`)
        .then((r) => setLoyalty(r.data?.found ? r.data : null))
        .catch(() => setLoyalty(null));
    }, 400);
    return () => clearTimeout(t);
  }, [customer.email]);

  const isAirport = (form.pickup_loc || "").toLowerCase() === "airport";
  const isPort    = (form.pickup_loc || "").toLowerCase() === "port";
  const dailyRate = Number(vehicle.daily_rate || 0);
  const baseTotal = Math.round(dailyRate * Math.max(days, 1));

  const extrasTotal = useMemo(() => {
    return extras
      .filter((e) => selectedExtras[e.id])
      .reduce((acc, e) => acc + (e.pricing_type === "per_day"
        ? Number(e.price || 0) * Math.max(days, 1)
        : Number(e.price || 0)), 0);
  }, [extras, selectedExtras, days]);

  // v3.9.6 — Tier discount applied on top of the base rate. Loyalty peek
  // returns the exact % (Silver 5, Gold 10, VIP 15) or 0 for Standard.
  const loyaltyDiscountAmount = Math.round(baseTotal * ((loyalty?.discount_pct || 0) / 100));

  const grandTotal = Math.max(0, baseTotal + extrasTotal - loyaltyDiscountAmount);

  const depositPct = Math.max(0, Math.min(100, Number(settings?.advance_deposit_pct ?? 30)));
  const depositAmount = Math.round(grandTotal * depositPct) / 100;
  const balanceDue    = Math.max(0, +(grandTotal - depositAmount).toFixed(2));
  const dueNow        = depositPct >= 100 ? grandTotal : depositAmount;

  const missingRequired = () => {
    if (!customer.full_name.trim()) return "Full name";
    if (!customer.email.trim())     return "Email";
    if (isRequiredStatus(cf.phone) && !phoneLocal.trim()) return "Mobile phone";
    for (const f of OPTIONAL_FIELDS) {
      if (isRequiredStatus(cf[f.key]) && !String(extra[f.key] || "").trim()) return f.label;
    }
    return null;
  };

  const copyIban = async () => {
    try {
      await navigator.clipboard.writeText(bank.iban || "");
      setIbanCopied(true);
      toast.success("IBAN copied to clipboard");
      setTimeout(() => setIbanCopied(false), 1800);
    } catch {
      toast.error("Copy failed — select the IBAN manually.");
    }
  };

  const submit = async () => {
    const missing = missingRequired();
    if (missing) {
      toast.error(`${missing} is required.`);
      return;
    }
    setSaving(true);
    try {
      const selectedIds = Object.entries(selectedExtras).filter(([, v]) => v).map(([k]) => k);
      const { data } = await api.post("/public/quick-book", {
        vehicle_id:      vehicle.id,
        pickup_date:     form.pickup_date,
        return_date:     form.return_date,
        pickup_time:     form.pickup_time,
        return_time:     form.return_time,
        pickup_location: form.pickup_loc,
        return_location: form.return_loc,
        full_name:       customer.full_name.trim(),
        email:           customer.email.trim(),
        phone:           phoneLocal.trim() ? `${dialCode} ${phoneLocal.trim()}` : "",
        addons:          selectedIds,
        source:          "internet",
        address:         extra.address || "",
        city:            extra.city || "",
        zip:             extra.zip || "",
        country:         extra.country || "",
        id_passport:     extra.id_passport || "",
        license_number:  extra.license_number || "",
        driver_age:      extra.driver_age ? Number(extra.driver_age) : null,
        notes:           extra.notes || "",
        airline_company: isAirport ? flight.airline_company : "",
        flight_number:   isAirport ? flight.flight_number   : "",
        flight_time:     isAirport ? flight.flight_time     : "",
        ferry_details:   isPort ? { ...ferry } : null,
        payment_method:  paymentMethod,
      });
      setConfirmed(data);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const activeOptionalFields = OPTIONAL_FIELDS.filter((f) => isActiveStatus(cf[f.key]));

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 overflow-y-auto p-4 flex justify-center items-start"
      onClick={onClose}
      data-testid="pb-booking-modal"
    >
      <div
        className="bg-white w-full max-w-4xl rounded-lg shadow-2xl my-6"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="px-6 py-4 border-b border-black/8 bg-neutral-50">
          <div className="flex items-start justify-between">
            <div className="flex-1 pr-3">
              <p className="text-[10px] tracking-[0.3em] uppercase text-neutral-500">Complete reservation</p>
              <h2 className="font-display text-2xl mt-1">{vehicle.name}</h2>
              {vehicle.description && (
                <p className="text-sm text-neutral-500 font-normal leading-relaxed mt-1 mb-2" data-testid="pb-modal-description">
                  {vehicle.description}
                </p>
              )}
            </div>
            <button type="button" onClick={onClose} className="p-2 hover:bg-black/5 rounded" data-testid="pb-modal-close">
              <X size={18} />
            </button>
          </div>
          <ModalHeroRow vehicle={vehicle} form={form} days={days} grandTotal={grandTotal} />
        </header>

        <div>
        {confirmed ? (
          <div className="p-8 text-center" data-testid="pb-confirmation">
            <CheckCircle2 size={40} className="text-emerald-600 mx-auto" />
            <h3 className="font-display text-2xl mt-3">Reservation confirmed.</h3>
            <p className="text-sm text-neutral-500 mt-2">Confirmation sent to <strong>{customer.email}</strong>.</p>
            <p className="mt-6 text-xs uppercase tracking-[0.3em] text-neutral-500">Booking reference</p>
            <p className="font-mono text-lg mt-1" data-testid="pb-booking-id">{confirmed.id?.slice(0, 8).toUpperCase()}</p>

            {/* v4.0.5 — Digital Rental Pass rendered inline on confirmation. */}
            <div className="mt-8">
              <DigitalRentalPass
                embedded
                companyName={settings?.company_name}
                booking={{
                  ...confirmed,
                  booking_reference: confirmed.id,
                  full_name:       customer.full_name,
                  customer_email:  customer.email,
                  vehicle_name:    vehicle.name,
                  pickup_date:     form.pickup_date,
                  return_date:     form.return_date,
                  pickup_time:     form.pickup_time,
                  return_time:     form.return_time,
                  pickup_location: form.pickup_loc,
                  return_location: form.return_loc,
                  id_passport:     extra.id_passport || "",
                  license_number:  extra.license_number || "",
                }}
              />
            </div>
            {confirmed.payment_method === "bank_transfer" && confirmed.bank_transfer_snapshot?.iban && (
              <p className="mt-4 text-xs text-neutral-500 max-w-md mx-auto">
                Bank transfer selected — please transfer <strong>{fmtEUR(confirmed.total)}</strong> to
                {" "}<span className="font-mono">{confirmed.bank_transfer_snapshot.iban}</span> using
                reference <strong>{confirmed.id?.slice(0, 8).toUpperCase()}</strong>.
              </p>
            )}
            <div className="mt-8">
              <button
                type="button"
                onClick={onClose}
                className="bg-black text-white text-sm px-6 py-2.5 rounded"
                data-testid="pb-done-btn"
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="p-6 space-y-5" data-testid="pb-step-1" style={{ display: step === 1 ? "block" : "none" }}>
              <h3
                className="uppercase tracking-wider text-xs md:text-sm font-semibold text-neutral-800 border-b border-neutral-200 pb-2 mb-4 mt-2"
                data-testid="pb-section-header-driver"
              >
                Driver & Reservation Details
              </h3>
              <section className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="pb-customer-section">
                <MFld label={requiredLabel("Full name", isRequiredStatus(cf.full_name))}>
                  <input className="pb-input" value={customer.full_name} onChange={(e) => setCustomer((c) => ({ ...c, full_name: e.target.value }))} data-testid="pb-cust-name" />
                </MFld>
                <MFld label={requiredLabel("Email", isRequiredStatus(cf.email))}>
                  <input type="email" className="pb-input" value={customer.email} onChange={(e) => setCustomer((c) => ({ ...c, email: e.target.value }))} data-testid="pb-cust-email" />
                  {loyalty && (
                    <span
                      className={`mt-1.5 inline-flex items-center gap-1 text-[10px] font-medium tracking-wide uppercase px-2 py-0.5 rounded ${
                        loyalty.tier === "VIP"    ? "bg-purple-200 text-purple-900"
                        : loyalty.tier === "Gold" ? "bg-amber-200 text-amber-900"
                        : loyalty.tier === "Silver" ? "bg-slate-200 text-slate-800"
                        : "bg-neutral-200 text-neutral-700"
                      }`}
                      data-testid="pb-loyalty-badge"
                    >
                      {loyalty.tier === "VIP" ? <Crown size={10} /> : <Award size={10} />}
                      Welcome back · {loyalty.tier}
                      {loyalty.discount_pct > 0 && <span className="ml-1">· {loyalty.discount_pct}% off</span>}
                    </span>
                  )}
                </MFld>
                <MFld label={requiredLabel("Mobile phone", isRequiredStatus(cf.phone))}>
                  <div
                    className="flex items-stretch rounded-lg border border-neutral-300 overflow-hidden focus-within:ring-2 focus-within:ring-neutral-900"
                    data-testid="pb-cust-phone-group"
                  >
                    <select
                      value={dialCode}
                      onChange={(e) => setDialCode(e.target.value)}
                      className="w-28 shrink-0 bg-neutral-50 border-r border-neutral-300 px-2 text-sm focus:outline-none tabular-nums"
                      data-testid="pb-cust-dialcode"
                      aria-label="Country calling code"
                    >
                      {DIAL_CODES.map((c) => (
                        <option key={c.iso} value={c.code}>
                          {c.code} ({c.iso})
                        </option>
                      ))}
                    </select>
                    <input
                      type="tel"
                      inputMode="numeric"
                      className="flex-1 min-w-0 border-0 px-3 py-2 text-sm focus:outline-none bg-white"
                      value={phoneLocal}
                      onChange={(e) => setPhoneLocal(e.target.value)}
                      placeholder="e.g. 6912345678"
                      data-testid="pb-cust-phone"
                    />
                  </div>
                </MFld>
              </section>

              {activeOptionalFields.length > 0 && (
                <section className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="pb-dynamic-fields">
                  {activeOptionalFields.map((f) => {
                    // v3.9.7 — Notes (textarea) spans the full row so
                    // the box aligns flush with the inputs above.
                    const wrapperClass = f.type === "textarea" ? "md:col-span-3" : "";
                    return (
                      <div key={f.key} className={wrapperClass}>
                        <MFld
                          label={requiredLabel(f.label, isRequiredStatus(cf[f.key]))}
                        >
                          {f.type === "textarea" ? (
                            <textarea
                              className="pb-input min-h-[80px] w-full"
                              rows={3}
                              value={extra[f.key] || ""}
                              onChange={(e) => setExtra((x) => ({ ...x, [f.key]: e.target.value }))}
                              data-testid={`pb-fld-${f.key}`}
                            />
                          ) : f.type === "country" ? (
                            <select
                              className="pb-input"
                              value={extra[f.key] || ""}
                              onChange={(e) => setExtra((x) => ({ ...x, [f.key]: e.target.value }))}
                              data-testid={`pb-fld-${f.key}`}
                            >
                              <option value="">Select country…</option>
                              {COUNTRIES.map((c) => (
                                <option key={c} value={c}>{c}</option>
                              ))}
                            </select>
                          ) : (
                            <input
                              type={f.type}
                              className="pb-input"
                              value={extra[f.key] || ""}
                              onChange={(e) => setExtra((x) => ({ ...x, [f.key]: e.target.value }))}
                              data-testid={`pb-fld-${f.key}`}
                            />
                          )}
                        </MFld>
                      </div>
                    );
                  })}
                </section>
              )}

              {extras.length > 0 && (
                <section className="border border-black/10 rounded p-4" data-testid="pb-extras-section">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-3">Extras &amp; insurance</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {extras.map((e) => {
                      const checked = !!selectedExtras[e.id];
                      const unit = e.pricing_type === "per_day" ? "/day" : "flat";
                      return (
                        <label
                          key={e.id}
                          className={`flex items-center justify-between px-3 py-2.5 rounded border cursor-pointer transition-colors ${checked ? "border-black bg-neutral-50" : "border-black/10 hover:border-black/30"}`}
                          data-testid={`pb-extra-${e.id}`}
                        >
                          <span className="flex items-center gap-2 text-sm">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => setSelectedExtras((s) => ({ ...s, [e.id]: !s[e.id] }))}
                              className="w-3.5 h-3.5"
                              data-testid={`pb-extra-cbx-${e.id}`}
                            />
                            {e.name}
                          </span>
                          <span className="text-xs tabular-nums text-neutral-600">
                            +{fmtEUR(e.price)}<span className="text-neutral-400"> {unit}</span>
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </section>
              )}

              {isAirport && (
                <section className="border border-black/10 rounded p-4" data-testid="pb-flight-card">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-3">Airport pickup — flight details</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <MFld label="Airline / Carrier"><input className="pb-input" value={flight.airline_company} onChange={(e) => setFlight((f) => ({ ...f, airline_company: e.target.value }))} data-testid="pb-flight-airline" /></MFld>
                    <MFld label="Flight number"><input className="pb-input" value={flight.flight_number} onChange={(e) => setFlight((f) => ({ ...f, flight_number: e.target.value }))} data-testid="pb-flight-number" /></MFld>
                    <MFld label="Arrival time"><input type="time" className="pb-input" value={flight.flight_time} onChange={(e) => setFlight((f) => ({ ...f, flight_time: e.target.value }))} data-testid="pb-flight-time" /></MFld>
                  </div>
                </section>
              )}
              {isPort && (
                <section className="border border-black/10 rounded p-4" data-testid="pb-ferry-card">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-3">Port pickup — ferry details</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <MFld label="Ferry company"><input className="pb-input" value={ferry.company} onChange={(e) => setFerry((f) => ({ ...f, company: e.target.value }))} data-testid="pb-ferry-company" /></MFld>
                    <MFld label="Vessel / ship name"><input className="pb-input" value={ferry.vessel_name} onChange={(e) => setFerry((f) => ({ ...f, vessel_name: e.target.value }))} data-testid="pb-ferry-vessel" /></MFld>
                    <MFld label="Arrival time"><input type="time" className="pb-input" value={ferry.arrival_time} onChange={(e) => setFerry((f) => ({ ...f, arrival_time: e.target.value }))} data-testid="pb-ferry-time" /></MFld>
                  </div>
                </section>
              )}

              {paymentMethods.length > 0 && (
                <section className="border border-black/10 rounded p-4" data-testid="pb-payment-section">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-3">
                    Payment method
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    {paymentMethods.map((m) => (
                      <PaymentPill
                        key={m}
                        method={m}
                        selected={paymentMethod === m}
                        onSelect={() => setPaymentMethod(m)}
                      />
                    ))}
                  </div>
                  {paymentMethod === "bank_transfer" && (bank.iban || bank.bank_name) && (
                    <div
                      className="mt-4 bg-neutral-50 border border-black/10 rounded p-4 text-sm"
                      data-testid="pb-bank-details-card"
                    >
                      <p className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-2">
                        Bank transfer details
                      </p>
                      <div className="space-y-1.5 tabular-nums">
                        {bank.bank_name && (
                          <p><span className="text-neutral-500 text-xs">Bank</span><br /><strong>{bank.bank_name}</strong></p>
                        )}
                        {bank.beneficiary && (
                          <p><span className="text-neutral-500 text-xs">Beneficiary</span><br /><strong>{bank.beneficiary}</strong></p>
                        )}
                        {bank.iban && (
                          <div>
                            <span className="text-neutral-500 text-xs">IBAN</span>
                            <div className="flex items-center gap-2 mt-1">
                              <code className="font-mono text-sm bg-white px-2 py-1 rounded border border-black/10 flex-1 break-all" data-testid="pb-bank-iban">{bank.iban}</code>
                              <button
                                type="button"
                                onClick={copyIban}
                                className="text-xs px-3 py-1.5 rounded border border-black/15 hover:border-black inline-flex items-center gap-1"
                                data-testid="pb-copy-iban"
                              >
                                {ibanCopied ? <Check size={12} /> : <Copy size={12} />} {ibanCopied ? "Copied" : "Copy"}
                              </button>
                            </div>
                          </div>
                        )}
                        {bank.swift && (
                          <p><span className="text-neutral-500 text-xs">SWIFT / BIC</span><br /><code className="font-mono">{bank.swift}</code></p>
                        )}
                        {bank.note && (
                          <p className="text-xs text-neutral-500 pt-2 border-t border-black/10 mt-2">{bank.note}</p>
                        )}
                      </div>
                    </div>
                  )}
                </section>
              )}
            </div>

            {step === 2 && (
              <div className="p-6 space-y-5" data-testid="pb-step-2">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="text-xs text-neutral-500 hover:text-black inline-flex items-center gap-1"
                  data-testid="pb-back-to-details"
                >
                  ← Edit details
                </button>

                <section className="border border-black/10 rounded p-4 flex gap-4" data-testid="pb-review-vehicle">
                  {(vehicle.gallery_images?.[0]?.url || vehicle.image_url) && (
                    <img
                      src={vehicle.gallery_images?.[0]?.url || vehicle.image_url}
                      alt={vehicle.name}
                      className="w-32 h-24 object-cover rounded"
                    />
                  )}
                  <div className="flex-1">
                    <p className="text-[10px] uppercase tracking-[0.25em] text-neutral-500">{vehicle.category}</p>
                    <p className="font-display text-lg mt-0.5">{vehicle.name}</p>
                    {vehicle.description && (
                      <p className="text-sm text-neutral-500 font-normal leading-relaxed mt-1">{vehicle.description}</p>
                    )}
                    <RentalSummaryBox
                      form={form} days={days} grandTotal={grandTotal}
                      testId="pb-review-summary" compact
                    />
                  </div>
                </section>

                {extras.filter((e) => selectedExtras[e.id]).length > 0 && (
                  <section className="border border-black/10 rounded p-4" data-testid="pb-review-extras">
                    <p className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-3">Selected extras</p>
                    <ul className="text-sm space-y-1">
                      {extras.filter((e) => selectedExtras[e.id]).map((e) => {
                        const price = e.pricing_type === "per_day"
                          ? Number(e.price || 0) * Math.max(days, 1)
                          : Number(e.price || 0);
                        return (
                          <li key={e.id} className="flex justify-between" data-testid={`pb-review-extra-${e.id}`}>
                            <span>{e.name}</span>
                            <span className="tabular-nums">{fmtEUR(price)}</span>
                          </li>
                        );
                      })}
                    </ul>
                  </section>
                )}

                <section className="border border-black/10 rounded p-4" data-testid="pb-review-payment">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-2">Payment method</p>
                  <p className="text-sm font-medium capitalize">
                    {paymentMethod === "pickup" ? "Pay on pickup" : paymentMethod === "card" ? "Card online" : "Bank transfer"}
                  </p>
                  {paymentMethod === "bank_transfer" && bank.iban && (
                    <div className="mt-3 pt-3 border-t border-black/10 text-xs space-y-1 text-neutral-600">
                      <p><strong>Bank:</strong> {bank.bank_name}</p>
                      <p><strong>Beneficiary:</strong> {bank.beneficiary}</p>
                      <p className="font-mono"><strong>IBAN:</strong> {bank.iban}</p>
                    </div>
                  )}
                </section>

                <section className="border border-black rounded-lg p-4 bg-neutral-50" data-testid="pb-review-breakdown">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-3">Payment breakdown</p>
                  <div className="space-y-2 text-sm tabular-nums">
                    <div className="flex justify-between">
                      <span>Rental base rate ({days} × {fmtEUR(dailyRate)})</span>
                      <span data-testid="pb-review-base">{fmtEUR(baseTotal)}</span>
                    </div>
                    {extrasTotal > 0 && (
                      <div className="flex justify-between">
                        <span>Selected extras</span>
                        <span data-testid="pb-review-extras-total">{fmtEUR(extrasTotal)}</span>
                      </div>
                    )}
                    {loyaltyDiscountAmount > 0 && (
                      <div className="flex justify-between text-emerald-700" data-testid="pb-review-loyalty">
                        <span>Loyalty · {loyalty?.tier} ({loyalty?.discount_pct}% off base)</span>
                        <span>−{fmtEUR(loyaltyDiscountAmount)}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-semibold border-t border-black/10 pt-2">
                      <span>Grand total</span>
                      <span data-testid="pb-review-grand-total">{fmtEUR(grandTotal)}</span>
                    </div>
                    <div className="flex justify-between text-emerald-700 pt-1">
                      <span>Deposit due now ({depositPct}%)</span>
                      <span data-testid="pb-review-deposit">{fmtEUR(depositAmount)}</span>
                    </div>
                    {balanceDue > 0 && depositPct < 100 && (
                      <div className="flex justify-between text-neutral-600">
                        <span>Balance due at pickup</span>
                        <span data-testid="pb-review-balance">{fmtEUR(balanceDue)}</span>
                      </div>
                    )}
                  </div>
                </section>
              </div>
            )}

            <footer className="px-6 py-4 border-t border-black/8 bg-neutral-50 flex justify-between items-center gap-3">
              <p className="text-xs text-neutral-500 flex-1">
                By booking you agree to our terms &amp; conditions.
              </p>
              {step === 1 ? (
                <button
                  type="button"
                  onClick={() => {
                    const missing = missingRequired();
                    if (missing) {
                      toast.error(`${missing} is required.`);
                      return;
                    }
                    setStep(2);
                  }}
                  className="bg-black text-white text-sm px-6 py-2.5 rounded inline-flex items-center gap-2"
                  data-testid="pb-review-btn"
                >
                  Review booking →
                </button>
              ) : (
                <button
                  type="button"
                  onClick={submit}
                  disabled={saving}
                  className="bg-black text-white text-sm px-6 py-2.5 rounded inline-flex items-center gap-2 disabled:opacity-50"
                  data-testid="pb-confirm-btn"
                >
                  {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                  {depositPct >= 100
                    ? `Confirm & pay · ${fmtEUR(grandTotal)}`
                    : `Confirm & pay deposit · ${fmtEUR(dueNow)}`}
                </button>
              )}
            </footer>
          </>
        )}
        </div>
      </div>
    </div>
  );
}
